import { useCallback, useEffect, useRef, useState } from "react";
import { cancelBookletJob, getBookletJob, isLiveJob } from "../../../services/bookletPrintService";

const POLL_MS = 2000;
// After so long the page stops asking (the job may still be waiting for a quiet server): "taking too long".
const LIMIT_MS = 15 * 60 * 1000;
// Reads that may fail in a row (a network blip) before the page says it lost track of the job.
const MAX_FAILURES = 3;

/**
 * Follows a booklet's print job. `latestUuid` is the booklet's latest job (booklet.documents.job.uuid): it is read
 * on mount, and whenever the booklet names another one that this page has not followed yet, and polled every 2 s
 * while it is queued or running. `onEnded(job)` is called when a job this page saw under way ends (succeeded, failed
 * or cancelled). Once the page is left, nothing is followed any more (a start or a stop answering late included).
 *
 * Returns {job, stalled, pollError, follow(jobOrUuid, {wasLive}), resume(), cancel()}: `stalled` after 15 minutes
 * of polling, `pollError` after reads failed in a row; resume() polls the same job again. cancel() rejects with
 * the request's error.
 */
export default function useBookletJob(latestUuid, onEnded) {
  const [job, setJob] = useState(null);
  const [stalled, setStalled] = useState(false);
  const [pollError, setPollError] = useState(null);
  // The job polled now: {uuid, started, live, failures}; a read answering for another one is dropped.
  const followRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  // Mounted: a request answering after the page was left follows nothing.
  const aliveRef = useRef(false);
  // The jobs this page has followed. The booklet's latest job only moves forward, so a booklet read naming one of
  // them answered late: the page has started (or been shown) a newer one meanwhile.
  const seenRef = useRef(new Set());
  const jobRef = useRef(job);
  jobRef.current = job;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const stop = useCallback(() => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();
    followRef.current = null;
  }, []);

  const poll = useCallback(async (state) => {
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const data = await getBookletJob(state.uuid, { signal: controller.signal });
      if (followRef.current !== state) return;
      state.failures = 0;
      setJob(data);
      if (!isLiveJob(data)) {
        followRef.current = null;
        if (state.live) onEndedRef.current?.(data);
        return;
      }
      state.live = true;
      if (Date.now() - state.started >= LIMIT_MS) {
        followRef.current = null;
        setStalled(true);
        return;
      }
    } catch (err) {
      if (controller.signal.aborted || followRef.current !== state) return;
      state.failures += 1;
      if (state.failures >= MAX_FAILURES) {
        followRef.current = null;
        setPollError(err);
        return;
      }
    }
    timerRef.current = setTimeout(() => poll(state), POLL_MS);
  }, []);

  // Follows `target`: a job as the API returned it (polled from the next tick if under way) or its uuid (read
  // first). `wasLive`: it was seen under way, so its end counts as an end (onEnded).
  const follow = useCallback(
    (target, { wasLive = false } = {}) => {
      stop();
      if (!aliveRef.current) return;
      setStalled(false);
      setPollError(null);
      const given = target && typeof target === "object" ? target : null;
      const uuid = given ? given.uuid : target;
      if (!uuid) return;
      seenRef.current.add(uuid);
      if (given) {
        setJob(given);
        if (!isLiveJob(given)) return;
      }
      const state = { uuid, started: Date.now(), live: wasLive || isLiveJob(given), failures: 0 };
      followRef.current = state;
      if (given) timerRef.current = setTimeout(() => poll(state), POLL_MS);
      else poll(state);
    },
    [poll, stop]
  );

  const resume = useCallback(() => {
    if (jobRef.current) follow(jobRef.current.uuid, { wasLive: true });
  }, [follow]);

  const cancel = useCallback(async () => {
    const current = jobRef.current;
    if (!current) return;
    const data = await cancelBookletJob(current.uuid);
    if (!aliveRef.current || !data || data.uuid !== jobRef.current?.uuid) return;
    setJob((prev) => (prev?.uuid === data.uuid ? { ...prev, ...data } : prev));
    if (!isLiveJob(data)) {
      // Queued: cancelled at once.
      stop();
      setStalled(false);
      setPollError(null);
      onEndedRef.current?.(data);
    } else if (!followRef.current) {
      // Not polled (taking too long, or reads failed): followed again to its end.
      follow(data, { wasLive: true });
    }
  }, [follow, stop]);

  // Before the effect below: React's development double mount runs them again in this order, the page mounted
  // (with nothing followed) again first.
  useEffect(() => {
    const seen = seenRef.current;
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      seen.clear();
      stop();
    };
  }, [stop]);

  // The booklet's latest job, on mount and when the booklet names another: read, and polled while under way.
  useEffect(() => {
    if (!latestUuid || seenRef.current.has(latestUuid)) return;
    follow(latestUuid);
  }, [latestUuid, follow]);

  return { job, stalled, pollError, follow, resume, cancel };
}
